import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Alert from '@components/Alert';

import { useSession } from '@hooks/useSession';

const messages = defineMessages({
  noChatRoom: {
    id: 'messenger.error.noChatRoom',
    defaultMessage: 'Chat room not found. Please contact support@vitall.com',
  },
  noChatUser: {
    id: 'messenger.error.noChatUser',
    defaultMessage: 'Chat user not found. Please contact support@vitall.com',
  },
  noConnection: {
    id: 'messenger.error.noConnection',
    defaultMessage: 'Connection not found. Please contact support@vitall.com',
  },
  default: {
    id: 'messenger.error.default',
    defaultMessage:
      'An error occurred while loading messenger. Please contact support@vitall.com',
  },
});

interface EthoraMessengerErrorProps {
  type: 'connection' | 'room' | 'user';
}

/**
 * If messenger has no chat user, no chat room, or no XMPP connection, this component renders custom text
 * instead of the Ethora login screen
 */
export default function EthoraMessengerError({
  type,
}: EthoraMessengerErrorProps) {
  const { formatMessage } = useIntl();
  const {
    state: { active_user_id, logged_in_user_id },
  } = useSession();

  useEffect(() => {
    Sentry.captureException('Messenger: No room/user/connection', {
      level: 'error',
      tags: { component: 'EthoraMessengerError', feature: 'messenger' },
      extra: { type, logged_in_user_id, active_user_id },
    });
  }, [active_user_id, logged_in_user_id, type]);

  const getMessage = () => {
    switch (type) {
      case 'connection':
        return messages.noConnection;
      case 'room':
        return messages.noChatRoom;
      case 'user':
        return messages.noChatUser;
      default:
        return messages.default;
    }
  };

  return <Alert color="error" title={formatMessage(getMessage())} />;
}
